import { useEffect, useState } from 'react';
import { Grid, TextInput, FileInput, Modal, Typography } from '@ts/storybook';
import { WrapperEntitySelector } from '@components/Inputs';
import { generateSearchEntitySelector } from '@modules/entitySelector/entitySelector.utils';

const FILESIZE_LIMIT_ICON = 20 * 1024; // 20*[Kb]
const FILESIZE_LIMIT_IMAGE = 10 * 1024 * 1024; // 10*[Mb]*[Kb]
const FILESIZE_LIMIT_DEFAULT = 1 * 1024 * 1024; //  1*[Mb]*[Kb]

interface ImageUploadProps {
  openModal: boolean;
  purposeId?: string;
  imageName?: string;
  onUploadPhoto: ({ imageName, purposeId, photoFile }) => void;
  onClose: () => void;
}

const ImageUpload = ({ openModal, purposeId = 'image', imageName, onUploadPhoto, onClose }: ImageUploadProps) => {
  const hideName = Boolean(imageName);
  const [state, setState] = useState<any>({
    imageDimensions: null,
    imageName: imageName || null,
    purposeId: purposeId || null,
    purpose: null,
    photoFile: null,
    photoFileURL: null,
  });

  const _handleUploadPhoto = () => {
    onUploadPhoto({
      photoFile: state.photoFile,
      imageName: state.imageName,
      purposeId: state.purposeId,
    });
  };

  const _handleChangeEntitySelector = (entity, entityName, fkField) => {
    const value = entity ? entity.getID() : null;
    const obj = {};
    obj[entityName] = entity;
    obj[fkField] = value;
    setState({ ...state, ...obj });
  };

  //#region Functions
  const getImageDimensions = (file, fileUrl) => {
    return new Promise(function (resolved, rejected) {
      let i = new Image();
      i.onload = function () {
        const size = { w: i.width, h: i.height, size: file.size };
        setState({ ...state, imageDimensions: size });
        resolved(size);
      };
      i.src = fileUrl;
    });
  };

  const checkConditionByPurpose = purposeId => {
    switch (purposeId) {
      case 'icon':
        return state.imageDimensions && state.imageDimensions.size > FILESIZE_LIMIT_ICON;
      case 'image':
        return state.imageDimensions && state.imageDimensions.size > FILESIZE_LIMIT_IMAGE;
      case 'default':
        return state.imageDimensions && state.imageDimensions.size > FILESIZE_LIMIT_DEFAULT;
      default:
        return true;
    }
  };
  //#endregion

  useEffect(() => {
    if (state.photoFile) {
      getImageDimensions(state.photoFile, state.photoFileURL);
    }
  }, [state.photoFile, state.photoFileURL]);

  useEffect(() => {
    setState({ ...state, imageName: imageName });
  }, [imageName]);

  return (
    <Modal
      withHeader
      title="Upload image"
      labelOk="Upload"
      onSubmit={_handleUploadPhoto}
      disabled={
        !state.photoFile ||
        !state.purposeId ||
        !state.imageName ||
        checkConditionByPurpose(state.purposeId) ||
        !state.imageDimensions
      }
      open={openModal}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
    >
      <Grid container spacing={2} p={2}>
        {!hideName && (
          <Grid size={{ xs: 12 }}>
            <TextInput
              label="Name"
              value={state.imageName}
              onChange={(ev, value) => {
                setState({ ...state, imageName: value });
              }}
              focus
              required
            />
          </Grid>
        )}
        {!purposeId && (
          <Grid size={{ xs: 12 }}>
            <WrapperEntitySelector
              searchComponent={generateSearchEntitySelector('purpose')}
              value={state.purpose}
              onChangeEntity={entity => _handleChangeEntitySelector(entity, 'purpose', 'purposeId')}
              label={'Purpose'}
              isReadOnly={purposeId ? true : false}
              required
            />
          </Grid>
        )}
        <Grid size={{ xs: 12 }}>
          <FileInput
            value={state.photoFile}
            onChange={(ev: React.ChangeEvent<HTMLInputElement>) => {
              if (ev.target.files && ev.target.files[0]) {
                const newState = {
                  photoFile: ev.target.files[0],
                  photoFileURL: URL.createObjectURL(ev.target.files[0]),
                };
                setState({ ...state, ...newState });
              }
            }}
            label={state.photoFile ? 'Change file' : 'Choose a file'}
            accept="*"
            hidden
            multiple
          />
        </Grid>
        <Grid size={{ xs: 12 }}>
          {state.photoFile && (
            <div style={{ textAlign: 'center' }}>
              <img className="max-w-[100px] max-h-[100px]" src={state.photoFileURL} />
            </div>
          )}
          {state.imageDimensions && (
            <div>
              <br />
              <Typography variant="body1">
                Size: {state.imageDimensions.w} by {state.imageDimensions.h} pixels
              </Typography>
              <br />
            </div>
          )}
          {state.purposeId === 'icon' && checkConditionByPurpose('icon') && (
            <Typography color="error" variant="body1">
              {Number(state.imageDimensions.size / 1024).toFixed(2)} Kb image is too big to be uploaded. Max size for Icons: 20Kb
            </Typography>
          )}
          {state.purposeId === 'image' && checkConditionByPurpose('image') && (
            <Typography color="error" variant="body1">
              {Number(state.imageDimensions.size / 1024 / 1024).toFixed(2)} Mb image is too big to be uploaded. Max size for
              general images: 10Mb
            </Typography>
          )}
          {state.purposeId === 'default' && checkConditionByPurpose('default') && (
            <Typography color="error" variant="body1">
              {Number(state.imageDimensions.size / 1024 / 1024).toFixed(2)} Mb image is too big to be uploaded. Max size for
              default images: 1Mb
            </Typography>
          )}
          {!state.imageDimensions && state.photoFileURL && (
            <Typography color="error" variant="body1" text="Not a valid image format." />
          )}
        </Grid>
      </Grid>
    </Modal>
  );
};

export default ImageUpload;
